import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";

// Fetch active bundles for user side
export const fetchBundles = createAsyncThunk(
  "bundle/fetchActive",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/bundles");
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Fetch bundles with pagination (user bundles page)
export const fetchBundlesWithPagination = createAsyncThunk(
  "bundle/fetchWithPagination",
  async (
    { page = 1, limit = 12, activeOnly, search = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit };
      if (activeOnly !== undefined) {
        params.activeOnly = activeOnly;
      }
      if (search) {
        params.search = search;
      }
      const res = await axiosInstance.get("/bundles", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Fetch paginated games for admin bundle picker
export const fetchBundlePickerGames = createAsyncThunk(
  "bundle/fetchPickerGames",
  async ({ page = 1, limit = 25, search = "" } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/getBundlePickerGames", {
        params: { page, limit, search },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Create a new bundle (Admins only)
export const createBundle = createAsyncThunk(
  "bundle/create",
  async (bundleData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/bundles", bundleData);
      toast.success(res.data.message || "Bundle created successfully");
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create bundle");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Update bundle (Admins only)
export const updateBundle = createAsyncThunk(
  "bundle/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/bundles/${id}`, data);
      toast.success(res.data.message || "Bundle updated successfully");
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update bundle");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Toggle bundle status (Admins only)
export const toggleBundleStatus = createAsyncThunk(
  "bundle/toggle",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/bundles/${id}/toggle`);
      toast.success(res.data.message || "Status updated successfully");
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to toggle status");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Delete a bundle (Admins only)
export const deleteBundle = createAsyncThunk(
  "bundle/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/bundles/${id}`);
      toast.success(res.data.message || "Bundle deleted successfully");
      return id;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete bundle");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const bundleSlice = createSlice({
  name: "bundle",
  initialState: {
    bundles: [],
    paginatedBundles: [],
    totalBundles: 0,
    totalPages: 1,
    loading: false,
    paginationLoading: false,
    pickerGames: [],
    pickerPage: 1,
    pickerTotalPages: 1,
    pickerTotal: 0,
    pickerLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Bundles
      .addCase(fetchBundles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBundles.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles = action.payload || [];
      })
      .addCase(fetchBundles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch bundles";
      })

      // Fetch Bundles With Pagination
      .addCase(fetchBundlesWithPagination.pending, (state) => {
        state.paginationLoading = true;
        state.error = null;
      })
      .addCase(fetchBundlesWithPagination.fulfilled, (state, action) => {
        state.paginationLoading = false;
        state.paginatedBundles = action.payload.data || [];
        state.totalBundles = action.payload.total || 0;
        state.totalPages = action.payload.totalPages || 1;
      })
      .addCase(fetchBundlesWithPagination.rejected, (state, action) => {
        state.paginationLoading = false;
        state.error = action.payload || "Failed to fetch bundles";
        state.paginatedBundles = [];
        state.totalBundles = 0;
        state.totalPages = 1;
      })

      // Fetch Bundle Picker Games
      .addCase(fetchBundlePickerGames.pending, (state) => {
        state.pickerLoading = true;
        state.error = null;
      })
      .addCase(fetchBundlePickerGames.fulfilled, (state, action) => {
        state.pickerLoading = false;
        state.pickerGames = action.payload.data || [];
        state.pickerPage = action.payload.page || 1;
        state.pickerTotalPages = action.payload.totalPages || 1;
        state.pickerTotal = action.payload.total || 0;
      })
      .addCase(fetchBundlePickerGames.rejected, (state, action) => {
        state.pickerLoading = false;
        state.error = action.payload || "Failed to load games";
        state.pickerGames = [];
        state.pickerPage = 1;
        state.pickerTotalPages = 1;
        state.pickerTotal = 0;
        toast.error(action.payload || "Failed to load games");
      })

      // Create Bundle
      .addCase(createBundle.pending, (state) => {
        state.loading = true;
      })
      .addCase(createBundle.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles.push(action.payload);
      })
      .addCase(createBundle.rejected, (state) => {
        state.loading = false;
      })

      // Update Bundle
      .addCase(updateBundle.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBundle.fulfilled, (state, action) => {
        state.loading = false;
        state.bundles = state.bundles.map((b) =>
          b._id === action.payload._id ? action.payload : b
        );
        state.paginatedBundles = state.paginatedBundles.map((b) =>
          b._id === action.payload._id ? action.payload : b
        );
      })
      .addCase(updateBundle.rejected, (state) => {
        state.loading = false;
      })

      // Toggle Status
      .addCase(toggleBundleStatus.fulfilled, (state, action) => {
        state.bundles = state.bundles.map((b) =>
          b._id === action.payload._id ? action.payload : b
        );
        state.paginatedBundles = state.paginatedBundles.map((b) =>
          b._id === action.payload._id ? action.payload : b
        );
      })

      // Delete Bundle
      .addCase(deleteBundle.fulfilled, (state, action) => {
        state.bundles = state.bundles.filter((b) => b._id !== action.payload);
        state.paginatedBundles = state.paginatedBundles.filter(
          (b) => b._id !== action.payload
        );
        state.totalBundles = Math.max(0, state.totalBundles - 1);
      });
  },
});

export default bundleSlice.reducer;
